import { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/outline';
import { ToolbarButton } from '@mui/x-data-grid';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import {
  ISavedFilterSchedule,
  isSchedulableSavedFilterType,
  isUserScopedSavedFilterType,
  SavedFilterType,
} from 'shared';

import { Modal } from '../../../../common/Atoms/Modal';
import { InputTextAddon } from '../../../../common/Atoms/InputTextAddon';
import { Button } from '../../../../common/Atoms/Button';
import { Accordian } from '../../../../common/Atoms/Accordian';
import { SavedFilterScheduleFields } from '../../Navigation/SavedFilterScheduleFields';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useCreateSavedFilterMutation } from '../../../services/api/savedFiltersApi/savedFilters';
import {
  exportFilterGridState,
  withExportColumns,
  MUI_DATAGRID_MAJOR_VERSION,
  toolbarButtonSx,
} from '../../../lib/datagrid';

interface SaveFilterButtonProps {
  type: SavedFilterType;
  /** Table state outside the grid to save alongside the filters (e.g. the actions status tab) */
  extraGridState?: Record<string, unknown>;
  /** The export API params for the current grid state, so the saved filter can be emailed on a schedule */
  exportParams?: Record<string, unknown>;
}

// Quick shortcut in the grid toolbar to save the current filters. The saved
// filters themselves live in the filter panel (ApiSavedFilterPanel).
export function SaveFilterButton({ type, extraGridState, exportParams }: SaveFilterButtonProps) {
  const currentOrg = useGetSelectedOrganisation();
  const apiRef = useGridApiContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [createSavedFilter, { isLoading }] = useCreateSavedFilterMutation();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState(``);
  const [schedule, setSchedule] = useState<ISavedFilterSchedule | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const schedulable = isSchedulableSavedFilterType(type) && !!exportParams;

  async function onSave() {
    if (!name.trim()) {
      toast.error(`Please enter a name for this saved filter`);

      return;
    }

    if (schedule && schedule.userIds.length === 0) {
      toast.error(`Choose at least one recipient`);

      return;
    }

    try {
      const created = await createSavedFilter({
        // User-scoped types (reports) have no org context
        organisationId: isUserScopedSavedFilterType(type) ? undefined : currentOrg?.id,
        name: name.trim(),
        type,
        gridState: exportFilterGridState(apiRef, extraGridState),
        gridVersion: MUI_DATAGRID_MAJOR_VERSION,
        exportParams: withExportColumns(apiRef, exportParams),
        schedule: schedulable ? schedule : undefined,
      }).unwrap();

      toast.success(`Saved "${name.trim()}" to your saved filters`);
      setName(``);
      setSchedule(null);
      setScheduleOpen(false);
      setOpen(false);

      // Mark the new saved filter as the active one
      navigate(`${location.pathname}?savedFilterId=${created.id}`);
    }
    catch (err) {
      toast.error(`Could not save filter. A saved filter with that name may already exist.`);
    }
  }

  return (
    <>
      <Tooltip title={ `Save the current filters as a view` }>
        <ToolbarButton
          onClick={ () => setOpen(true) }
          material={ { sx: toolbarButtonSx } }
        >
          <StarIcon className={ `h-4 w-4` } />
          { `Save view` }
        </ToolbarButton>
      </Tooltip>

      <Modal title={ `Save filter` }
        open={ open }
        setOpen={ setOpen }>
        <div className={ `space-y-4 min-w-[350px]` }>
          <InputTextAddon
            label={ `Filter name` }
            name={ `savedFilterName` }
            value={ name }
            placeholder={ `e.g. Big debtors 60+ days` }
            required
            onChange={ value => setName(value?.value?.toString() || ``) }
          />
          { schedulable && (
            <Accordian
              title={ `Scheduled email` }
              description={ schedule ? `${schedule.intervalType === `weekly` ? `Weekly` : `Monthly`} email set up` : `Optionally email a CSV of this view on a schedule` }
              open={ scheduleOpen }
              setOpen={ () => setScheduleOpen(!scheduleOpen) }
            >
              <div className={ `space-y-4` }>
                <SavedFilterScheduleFields schedule={ schedule }
                  onChange={ setSchedule } />
              </div>
            </Accordian>
          ) }
          <div className={ `flex justify-end` }>
            <Button onClick={ onSave }
              loading={ isLoading }
              loadingText={ `Saving...` }>
              { `Save filter` }
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
